import { Injectable } from '@angular/core';
import { HorizontalLine, SystemLine, RecentTicker } from '../chart.interface';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly STORAGE_KEYS = {
    RECENT_TICKERS: 'tradingview_recent_tickers',
    HORIZONTAL_LINES: 'horizontal_lines',
    SYSTEM_LINES: 'system_lines',
    SYSTEM_LINE_VISIBILITY: 'system_line_visibility',
    MARKER_VISIBILITY: 'marker_visibility',
    SMA_VISIBILITY: 'sma_visibility',
    ATR_VISIBILITY: 'atr_visibility',
    CHART_SETTINGS: 'chart_settings'
  };

  private readonly MAX_RECENT_TICKERS = 10;

  constructor() {}

  // === Recent Tickers Management ===

  loadRecentTickers(): RecentTicker[] {
    const stored = localStorage.getItem(this.STORAGE_KEYS.RECENT_TICKERS);
    if (stored && stored.trim().length > 0) {
      try {
        const parsed = JSON.parse(stored);
        
        // Validate parsed data structure
        if (!Array.isArray(parsed)) {
          console.warn('[WARNING] Stored recent tickers data is not an array');
          return [];
        }
        
        return parsed.map((ticker: any) => {
          if (!ticker || typeof ticker !== 'object') {
            console.warn('[WARNING] Invalid ticker item found, skipping');
            return null;
          }
          
          return {
            ...ticker,
            lastViewed: ticker.lastViewed ? new Date(ticker.lastViewed) : new Date()
          };
        }).filter((ticker: any) => ticker !== null);
      } catch (error) {
        console.error('Error loading recent tickers:', error);
        return [];
      }
    }
    return [];
  }

  saveRecentTickers(tickers: RecentTicker[]): void {
    // Null safety check for tickers
    if (!Array.isArray(tickers)) {
      console.warn('[WARNING] Invalid tickers array provided to saveRecentTickers');
      return;
    }

    try {
      localStorage.setItem(this.STORAGE_KEYS.RECENT_TICKERS, JSON.stringify(tickers));
    } catch (error) {
      console.error('Error saving recent tickers:', error);
    }
  }

  addRecentTicker(ticker: RecentTicker): void {
    // Null safety check for ticker
    if (!ticker || typeof ticker !== 'object' || !ticker.symbol) {
      console.warn('[WARNING] Invalid ticker provided to addRecentTicker');
      return;
    }

    const tickers = this.loadRecentTickers();
    
    // Remove existing entry if it exists
    const filtered = tickers.filter(t => t && t.symbol !== ticker.symbol);
    
    // Add to the beginning
    filtered.unshift(ticker);
    
    // Keep only the most recent MAX_RECENT_TICKERS
    const trimmed = filtered.slice(0, this.MAX_RECENT_TICKERS);
    
    this.saveRecentTickers(trimmed);
  }

  removeRecentTicker(symbol: string): void {
    // Null safety check for symbol
    if (!symbol || typeof symbol !== 'string' || symbol.trim().length === 0) {
      console.warn('[WARNING] Invalid symbol provided to removeRecentTicker');
      return;
    }

    const tickers = this.loadRecentTickers();
    const filtered = tickers.filter(t => t && t.symbol !== symbol);
    this.saveRecentTickers(filtered);
  }

  clearRecentTickers(): void {
    localStorage.removeItem(this.STORAGE_KEYS.RECENT_TICKERS);
  }

  // === Horizontal Lines Management ===

  loadHorizontalLines(symbol: string): HorizontalLine[] {
    try {
      if (!symbol || typeof symbol !== 'string' || symbol.trim().length === 0) {
        console.warn('[WARNING] Invalid symbol provided to loadHorizontalLines');
        return [];
      }

      const key = `${this.STORAGE_KEYS.HORIZONTAL_LINES}_${symbol}`;
      const stored = localStorage.getItem(key);
      if (stored && stored.trim().length > 0) {
        try {
          const parsed = JSON.parse(stored);
          if (!Array.isArray(parsed)) {
            console.warn('[WARNING] Stored horizontal lines data is not an array');
            return [];
          }
          return parsed;
        } catch (parseError) {
          console.error('[ERROR] Failed to parse stored horizontal lines:', parseError);
          return [];
        }
      }
      return [];
    } catch (error) {
      console.error('[ERROR] Error loading horizontal lines:', error);
      return [];
    }
  }

  saveHorizontalLines(symbol: string, lines: HorizontalLine[]): void {
    try {
      if (!symbol || typeof symbol !== 'string' || symbol.trim().length === 0) {
        console.warn('[WARNING] Invalid symbol provided to saveHorizontalLines');
        return;
      }

      if (!Array.isArray(lines)) {
        console.warn('[WARNING] Invalid lines array provided to saveHorizontalLines');
        return;
      }

      const key = `${this.STORAGE_KEYS.HORIZONTAL_LINES}_${symbol}`;
      localStorage.setItem(key, JSON.stringify(lines));
    } catch (error) {
      console.error('[ERROR] Error saving horizontal lines:', error);
    }
  }

  clearHorizontalLines(symbol: string): void {
    try {
      if (!symbol || typeof symbol !== 'string' || symbol.trim().length === 0) {
        console.warn('[WARNING] Invalid symbol provided to clearHorizontalLines');
        return;
      }

      const key = `${this.STORAGE_KEYS.HORIZONTAL_LINES}_${symbol}`;
      localStorage.removeItem(key);
    } catch (error) {
      console.error('[ERROR] Error clearing horizontal lines:', error);
    }
  }

  // === System Lines Management ===

  loadSystemLines(symbol: string): SystemLine[] {
    const key = `${this.STORAGE_KEYS.SYSTEM_LINES}_${symbol}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error loading system lines:', error);
        return [];
      }
    }
    return [];
  }

  saveSystemLines(symbol: string, lines: SystemLine[]): void {
    try {
      const key = `${this.STORAGE_KEYS.SYSTEM_LINES}_${symbol}`;
      localStorage.setItem(key, JSON.stringify(lines));
    } catch (error) {
      console.error('Error saving system lines:', error);
    }
  }

  loadSystemLineVisibility(symbol: string): Record<SystemLine['type'], boolean> | null {
    const key = `${this.STORAGE_KEYS.SYSTEM_LINE_VISIBILITY}_${symbol}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error loading system line visibility:', error);
        return null;
      }
    }
    return null;
  }

  saveSystemLineVisibility(symbol: string, visibility: Record<SystemLine['type'], boolean>): void {
    try {
      const key = `${this.STORAGE_KEYS.SYSTEM_LINE_VISIBILITY}_${symbol}`;
      localStorage.setItem(key, JSON.stringify(visibility));
    } catch (error) {
      console.error('Error saving system line visibility:', error);
    }
  }

  clearSystemLines(symbol: string): void {
    const systemKey = `${this.STORAGE_KEYS.SYSTEM_LINES}_${symbol}`;
    const visibilityKey = `${this.STORAGE_KEYS.SYSTEM_LINE_VISIBILITY}_${symbol}`;
    localStorage.removeItem(systemKey);
    localStorage.removeItem(visibilityKey);
  }

  // === Marker Visibility Management ===

  loadMarkerVisibility(): Record<string, boolean> | null {
    const stored = localStorage.getItem(this.STORAGE_KEYS.MARKER_VISIBILITY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error loading marker visibility:', error);
        return null;
      }
    }
    return null;
  }

  saveMarkerVisibility(visibility: Record<string, boolean>): void {
    try {
      localStorage.setItem(this.STORAGE_KEYS.MARKER_VISIBILITY, JSON.stringify(visibility));
    } catch (error) {
      console.error('Error saving marker visibility:', error);
    }
  }

  // === SMA Visibility Management ===

  loadSMAVisibility(): Record<string, boolean> | null {
    const stored = localStorage.getItem(this.STORAGE_KEYS.SMA_VISIBILITY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error loading SMA visibility:', error);
        return null;
      }
    }
    return null;
  }

  saveSMAVisibility(visibility: Record<string, boolean>): void {
    try {
      localStorage.setItem(this.STORAGE_KEYS.SMA_VISIBILITY, JSON.stringify(visibility));
    } catch (error) {
      console.error('Error saving SMA visibility:', error);
    }
  }

  // === ATR Visibility Management ===

  loadATRVisibility(): Record<string, boolean> | null {
    const stored = localStorage.getItem(this.STORAGE_KEYS.ATR_VISIBILITY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error loading ATR visibility:', error);
        return null;
      }
    }
    return null;
  }

  saveATRVisibility(visibility: Record<string, boolean>): void {
    try {
      localStorage.setItem(this.STORAGE_KEYS.ATR_VISIBILITY, JSON.stringify(visibility));
    } catch (error) {
      console.error('Error saving ATR visibility:', error);
    }
  }

  // === Chart Settings Management ===

  loadChartSettings(): any {
    const stored = localStorage.getItem(this.STORAGE_KEYS.CHART_SETTINGS);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error loading chart settings:', error);
        return {};
      }
    }
    return {};
  }

  saveChartSettings(settings: any): void {
    try {
      localStorage.setItem(this.STORAGE_KEYS.CHART_SETTINGS, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving chart settings:', error);
    }
  }

  // === Utility Methods ===

  getStorageUsage(): { used: number; total: number; percentage: number } {
    let used = 0;
    
    // Calculate used storage
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        used += localStorage[key].length;
      }
    }
    
    // Estimate total available (5MB typical limit)
    const total = 5 * 1024 * 1024; // 5MB in bytes
    const percentage = (used / total) * 100;
    
    return {
      used: used,
      total: total,
      percentage: percentage
    };
  }

  clearAllChartData(): void {
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith(this.STORAGE_KEYS.HORIZONTAL_LINES) ||
      key.startsWith(this.STORAGE_KEYS.SYSTEM_LINES) ||
      key.startsWith(this.STORAGE_KEYS.SYSTEM_LINE_VISIBILITY)
    );
    
    keys.forEach(key => localStorage.removeItem(key));
  }

  exportSettings(): string {
    const settings = {
      recentTickers: this.loadRecentTickers(),
      markerVisibility: this.loadMarkerVisibility(),
      // Legacy visibility settings (migrated to new indicator system)
      // smaVisibility: this.loadSMAVisibility(),
      // atrVisibility: this.loadATRVisibility(),
      chartSettings: this.loadChartSettings()
    };
    
    return JSON.stringify(settings, null, 2);
  }

  importSettings(settingsJson: string): boolean {
    try {
      const settings = JSON.parse(settingsJson);
      
      if (settings.recentTickers) {
        this.saveRecentTickers(settings.recentTickers);
      }
      
      if (settings.markerVisibility) {
        this.saveMarkerVisibility(settings.markerVisibility);
      }
      
      // Legacy visibility settings (migrated to new indicator system)
      // if (settings.smaVisibility) {
      //   this.saveSMAVisibility(settings.smaVisibility);
      // }
      
      // if (settings.atrVisibility) {
      //   this.saveATRVisibility(settings.atrVisibility);
      // }
      
      if (settings.chartSettings) {
        this.saveChartSettings(settings.chartSettings);
      }
      
      return true;
    } catch (error) {
      console.error('Error importing settings:', error);
      return false;
    }
  }

  
    public putObject(key: string, object: any) {
      return localStorage.setItem(key, JSON.stringify(object));
    }
  
    public getObject(key: string) {
      const values = localStorage.getItem(key);
      try {
        return values ? JSON.parse(values) : null;
      } catch (error) {
        return values;
      }
    }
  
    public set(key: string, value: string) {
      return localStorage.setItem(key, value);
    }
  
    public get(key: string) {
      return localStorage.getItem(key);
    }
  
    public async remove(key: string) {
      return localStorage.removeItem(key);
    }
  
    public clear() {
      return localStorage.clear();
    }
  
} 